import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from "recharts";
import axios from "axios";
import API_BASE_URL from "../../../config";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import "../../../Styles/Admin/assessment/AssessmentDashboard.css";

// ---------------- Constants ----------------

// Month abbreviations (3 letters) used for the Company Overview labels
const monthAbbr = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Colours for the Assessments Overview donut (labels/values come from real data)
const ASSESSMENT_STATUS_META = [
  { name: "Completed", color: "#22C55E" },
  { name: "In Progress", color: "#3B82F6" },
  { name: "Not Started", color: "#FACC15" },
];

const ALL = "All";

// Each company -> { y: assessment year, m: month it was added (0-11), d: day it was added }
// Returns null when the company has no usable year / created_at.
const companyMonthKey = (c) => {
  const y = Number(c.year);
  if (!y || !c.created_at) return null;
  const added = new Date(c.created_at);
  if (isNaN(added)) return null;
  return { y, m: added.getMonth(), d: added.getDate() };
};

// ---------------- Icons (inline SVG, no external dependency) ----------------

const AdbIconLogout = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path
      d="M15 17l5-5-5-5M20 12H9M13 4H6a2 2 0 00-2 2v12a2 2 0 002 2h7"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AdbIconChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Warning-triangle icon used inside the Delete confirmation popup.
const AdbIconWarning = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 3.5l9.5 16.5H2.5L12 3.5z"
      stroke="#DC2626"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M12 10v4" stroke="#DC2626" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="17" r="0.9" fill="#DC2626" />
  </svg>
);

// ---------------- Status badge helper ----------------

const statusMeta = {
  "Not Started": { dot: "#F59E0B", text: "#B45309", bg: "#FEF3C7" },
  Completed: { dot: "#22C55E", text: "#15803D", bg: "#DCFCE7" },
  "In Progress": { dot: "#3B82F6", text: "#1D4ED8", bg: "#DBEAFE" },
};

const progressColor = {
  "Not Started": "#E5E7EB",
  Completed: "#22C55E",
  "In Progress": "#3B82F6",
};

// An employee's assessment status, tolerant of how the backend labels it.
const getEmployeeStatus = (emp) => {
  const status = emp.assessment_status;
  const progress = Number(emp.progress) || 0;
  if (status === "Completed") return "Completed";
  if (status === "In Progress" || progress > 0) return "In Progress";
  return "Not Started";
};

// ---------------- Small dropdown used by the three filters ----------------
// options = [{ value, label }]. Closes on outside click.

const AdbSelect = ({ icon, iconAlt, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className="adb-dropdown-wrapper" ref={ref}>
      <button
        className="adb-dropdown-btn"
        type="button"
        onClick={() => setOpen((o) => !o)}
      >
        <img src={icon} alt={iconAlt} className="adb-dropdown-icon" />
        <span>{selected ? selected.label : ""}</span>
        <AdbIconChevronDown />
      </button>
      {open && (
        <ul
          className="adb-dropdown-list"
          style={{ maxHeight: 260, overflowY: "auto" }}
        >
          {options.map((o) => (
            <li
              key={o.value}
              className={
                o.value === value
                  ? "adb-dropdown-list-item adb-dropdown-list-item-active"
                  : "adb-dropdown-list-item"
              }
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------- Main Component ----------------

const AssessmentDashboardPage = () => {
  // Company Overview date filter (calendar picker). "" = All Months.
  // A value like "2026-11-04" shows the date-wise view of that month.
  const [selectedDate, setSelectedDate] = useState("");
  // Assessments Overview filters.
  const [assessmentYear, setAssessmentYear] = useState(ALL);
  const [selectedCompany, setSelectedCompany] = useState(ALL);
  const navigate = useNavigate();

  const handleLogout = () => {
    // Remove the admin login token (use the same key your admin login saves)
    localStorage.removeItem("adminToken");
    navigate("/admin/assessment/login");
  };

  const [totalCategories, setTotalCategories] = useState(0);
  const [newCategoriesThisMonth, setNewCategoriesThisMonth] = useState(0);

  const [totalCompanies, setTotalCompanies] = useState(0);
  const [newCompaniesThisMonth, setNewCompaniesThisMonth] = useState(0);
  const [companiesData, setCompaniesData] = useState([]); // every company record (one per company + year)

  const [totalEmployees, setTotalEmployees] = useState(0);
  const [newEmployeesThisMonth, setNewEmployeesThisMonth] = useState(0);
  const [employeesData, setEmployeesData] = useState([]); // every employee, with assessment status

  const [reportStatusData, setReportStatusData] = useState([]); // per-company progress numbers

  // ---- Edit / Delete modal state for the Recent Companies table ----
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRowForAction, setSelectedRowForAction] = useState(null);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editAssessmentYear, setEditAssessmentYear] = useState("");
  const [editEmployees, setEditEmployees] = useState("");

  // ---- Categories stats ----
  useEffect(() => {
    const fetchCategoryStats = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/quizzes`);
        const quizzes = res.data || [];

        setTotalCategories(quizzes.length);

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const addedThisMonth = quizzes.filter((quiz) => {
          if (!quiz.date_added) return false;
          const added = new Date(quiz.date_added);
          return (
            added.getMonth() === currentMonth &&
            added.getFullYear() === currentYear
          );
        });

        setNewCategoriesThisMonth(addedThisMonth.length);
      } catch (err) {
        console.error(err);
      }
    };

    fetchCategoryStats();
  }, []);

  // ---- Companies + employees + progress (feeds the stat cards, both charts and the table) ----
  const loadData = useCallback(async () => {
    const [companiesRes, employeesRes, statusRes] = await Promise.all([
      axios.get(`${API_BASE_URL}/api/assessment/companies/list`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE_URL}/api/employees`).catch(() => ({ data: [] })),
      axios.get(`${API_BASE_URL}/api/employees/report-status`).catch(() => ({ data: [] })),
    ]);

    const companiesList = companiesRes.data || [];
    const employeesList = employeesRes.data || [];

    setCompaniesData(companiesList);
    setEmployeesData(employeesList);
    setReportStatusData(statusRes.data || []);

    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const isThisMonth = (value) => {
      if (!value) return false;
      const added = new Date(value);
      return added.getMonth() === currentMonth && added.getFullYear() === currentYear;
    };

    setTotalCompanies(companiesList.length);
    setNewCompaniesThisMonth(companiesList.filter((c) => isThisMonth(c.created_at)).length);

    setTotalEmployees(employeesList.length);
    setNewEmployeesThisMonth(employeesList.filter((e) => isThisMonth(e.created_at)).length);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---------------- Filter options (all built from real data) ----------------

  // Every assessment year that exists, newest first (used by Assessments Overview).
  const availableYears = useMemo(
    () =>
      Array.from(
        new Set(companiesData.map((c) => Number(c.year)).filter(Boolean))
      ).sort((a, b) => b - a),
    [companiesData]
  );

  const yearOptions = useMemo(
    () => [
      { value: ALL, label: "All Years" },
      ...availableYears.map((y) => ({ value: String(y), label: `Year ${y}` })),
    ],
    [availableYears]
  );

  // Company list for the Assessments Overview dropdown. When a year is picked,
  // only companies that have a record in that year are offered.
  const companyOptions = useMemo(() => {
    const names = Array.from(
      new Set(
        companiesData
          .filter((c) => assessmentYear === ALL || String(c.year) === assessmentYear)
          .map((c) => c.name)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
    return [
      { value: ALL, label: "All Companies" },
      ...names.map((n) => ({ value: n, label: n })),
    ];
  }, [companiesData, assessmentYear]);

  // If the chosen company has no record in the newly chosen year, fall back to All.
  const activeCompany = companyOptions.some((o) => o.value === selectedCompany)
    ? selectedCompany
    : ALL;

  // ---------------- Company Overview chart ----------------
  // "All Months": one point per month that has companies, labelled like
  //   "Jun 26" / "Jul 27" (assessment year + month the company was added).
  // A single month: date-wise points (1, 2, 3 ... last day of that month).
  const companyOverviewChartData = useMemo(() => {
    // blank first/last columns keep the first and last real points off the chart edges
    const pad = (points) => [{ month: "", value: null }, ...points, { month: " ", value: null }];

    if (!selectedDate) {
      const counts = new Map();
      companiesData.forEach((c) => {
        const k = companyMonthKey(c);
        if (!k) return;
        const key = `${k.y}-${k.m}`;
        counts.set(key, { ...k, count: (counts.get(key)?.count || 0) + 1 });
      });

      const points = Array.from(counts.values())
        .sort((a, b) => a.y - b.y || a.m - b.m)
        .map((k) => ({
          month: `${monthAbbr[k.m]} ${String(k.y).slice(-2)}`,
          value: k.count,
        }));

      return points.length ? pad(points) : [];
    }

    const [selY, selMonth] = selectedDate.split("-").map(Number);
    const selM = selMonth - 1;
    const daysInMonth = new Date(selY, selM + 1, 0).getDate();
    const countsByDay = new Array(daysInMonth).fill(0);
    companiesData.forEach((c) => {
      const k = companyMonthKey(c);
      if (k && k.y === selY && k.m === selM) countsByDay[k.d - 1] += 1;
    });

    return pad(
      countsByDay.map((value, i) => ({
        month: `${i + 1} ${monthAbbr[selM]}`,
        value,
      }))
    );
  }, [companiesData, selectedDate]);

  // Label of the picked day on the X axis (e.g. "4 Nov"), used for the highlight line.
  const selectedDayLabel = selectedDate
    ? (() => {
        const [, m, d] = selectedDate.split("-").map(Number);
        return `${d} ${monthAbbr[m - 1]}`;
      })()
    : null;

  // Evenly spaced Y-axis ticks that adapt to the data range.
  const companyOverviewYTicks = useMemo(() => {
    const dataMax = Math.max(0, ...companyOverviewChartData.map((d) => d.value || 0));
    let step;
    if (dataMax <= 5) step = 1;
    else if (dataMax <= 10) step = 2;
    else if (dataMax <= 20) step = 3;
    else step = Math.ceil(dataMax / 6);

    const topTick = Math.max(step * 3, Math.ceil((dataMax + 1) / step) * step);
    const ticks = [];
    for (let i = 0; i <= topTick; i += step) ticks.push(i);
    return ticks;
  }, [companyOverviewChartData]);

  // Enough horizontal room per column so labels never overlap; scrolls inside the card.
  // (A 30-day month gets 70px per day; lower/raise the number to taste.)
  const companyOverviewChartWidth = Math.max(
    600,
    companyOverviewChartData.length * (!selectedDate ? 90 : 70)
  );

  // ---------------- Assessments Overview donut ----------------
  // Attendees = employees of the companies that match the year + company filters.
  const assessmentOverviewData = useMemo(() => {
    const matchingCompanies = companiesData.filter(
      (c) =>
        (assessmentYear === ALL || String(c.year) === assessmentYear) &&
        (activeCompany === ALL || c.name === activeCompany)
    );
    const matchingIds = new Set(matchingCompanies.map((c) => String(c.id)));

    const matchingEmployees = employeesData.filter((e) => {
      if (e.company_id != null) return matchingIds.has(String(e.company_id));
      // fallback for employee rows that don't carry a company id
      return (
        (assessmentYear === ALL || String(e.company_year) === assessmentYear) &&
        (activeCompany === ALL || e.company_name === activeCompany)
      );
    });

    const counts = { Completed: 0, "In Progress": 0, "Not Started": 0 };
    matchingEmployees.forEach((e) => {
      counts[getEmployeeStatus(e)] += 1;
    });

    const total = matchingEmployees.length;
    return ASSESSMENT_STATUS_META.map((meta) => ({
      ...meta,
      value: counts[meta.name],
      percent: total > 0 ? Math.round((counts[meta.name] / total) * 100) : 0,
    }));
  }, [companiesData, employeesData, assessmentYear, activeCompany]);

  const totalAttendees = assessmentOverviewData.reduce((sum, d) => sum + d.value, 0);

  // Grey ring when nobody matches, so the donut never disappears.
  const donutData =
    totalAttendees > 0
      ? assessmentOverviewData
      : [{ name: "No data", value: 1, color: "#E5E7EB" }];

  // ---------------- Recent Companies table (latest 4 added) ----------------
  const recentCompanies = useMemo(() => {
    const statusByCompany = {};
    reportStatusData.forEach((s) => {
      statusByCompany[String(s.company_id)] = s;
    });

    return [...companiesData]
      .sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime || Number(b.id) - Number(a.id);
      })
      .slice(0, 4)
      .map((c, idx) => {
        const s = statusByCompany[String(c.id)] || {};
        const target = s.target_employees ?? c.employees_count ?? 0;
        const completed = s.completed_employees || 0;
        const started = s.started_employees || 0;

        let status = "Not Started";
        let progress = 0;
        if (target > 0 && completed >= target) {
          status = "Completed";
          progress = 100;
        } else if (started > 0) {
          status = "In Progress";
          progress = target > 0 ? Math.round((completed / target) * 100) : 0;
        }

        return {
          id: c.id,
          sNo: String(idx + 1).padStart(2, "0"),
          company: c.name,
          year: c.year,
          employees: target,
          status,
          progress,
        };
      });
  }, [companiesData, reportStatusData]);

  // ---- Edit popup open/close + save handlers ----
  const openEditModal = (row) => {
    setSelectedRowForAction(row);
    setEditCompanyName(row.company);
    setEditAssessmentYear(String(row.year ?? ""));
    setEditEmployees(String(row.employees ?? ""));
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRowForAction(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedRowForAction) return;
    // Use the full company record so fields this popup doesn't edit are kept as they are.
    const record =
      companiesData.find((c) => String(c.id) === String(selectedRowForAction.id)) || {};
    try {
      await axios.post(
        `${API_BASE_URL}/api/assessment/companies/edit/${selectedRowForAction.id}`,
        {
          name: editCompanyName,
          year: parseInt(editAssessmentYear, 10) || record.year,
          employees_count: parseInt(editEmployees, 10) || 0,
          status: record.status ?? 1,
          company_url: record.company_url || "",
          company_full_url: record.company_full_url || "",
          username: record.username || "",
        }
      );
      closeEditModal();
      loadData();
    } catch (err) {
      console.error("Failed to update company:", err);
    }
  };

  // ---- Delete popup open/close + confirm handlers ----
  const openDeleteModal = (row) => {
    setSelectedRowForAction(row);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedRowForAction(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRowForAction) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/assessment/companies/delete/${selectedRowForAction.id}`
      );
      closeDeleteModal();
      loadData();
    } catch (err) {
      console.error("Failed to delete company:", err);
    }
  };

  return (
    <div className="adb-page-layout">
      <Sidebar />
      <div className="adb-dashboard-content">
        {/* ---------------- Top Header ---------------- */}
        <div className="adb-dashboard-header-card">
          <div className="adb-dashboard-header">
            <div className="adb-dashboard-header-left">
              <h1 className="adb-dashboard-title">Welcome Back, Admin!</h1>
              <p className="adb-dashboard-subtitle">
                Here's what's happening with your organizations.
              </p>
            </div>
            <div className="adb-dashboard-header-right">
              <button className="adb-logout-btn" type="button" onClick={handleLogout}>
                <AdbIconLogout />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* ---------------- Stat Cards Row ---------------- */}
        <div className="adb-stats-row">
          <div className="adb-stat-card">
            <div className="adb-stat-icon-box adb-stat-icon-orange">
              <img src="/Ass_db_icon_01.png" alt="Categories" />
            </div>
            <div className="adb-stat-info">
              <h2 className="adb-stat-number">{totalCategories}</h2>
              <p className="adb-stat-label">
                Total categories
                {newCategoriesThisMonth > 0 && (
                  <span className="adb-stat-delta adb-stat-delta-positive">
                    {" "}+{newCategoriesThisMonth} this month
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="adb-stat-card">
            <div className="adb-stat-icon-box adb-stat-icon-blue">
              <img src="/Ass_db_icon_02.png" alt="Companies" />
            </div>
            <div className="adb-stat-info">
              <h2 className="adb-stat-number">{totalCompanies}</h2>
              <p className="adb-stat-label">
                Companies
                {newCompaniesThisMonth > 0 && (
                  <span className="adb-stat-delta adb-stat-delta-positive">
                    {" "}+{newCompaniesThisMonth} this month
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="adb-stat-card">
            <div className="adb-stat-icon-box adb-stat-icon-purple">
              <img src="/Ass_db_icon_03.png" alt="Employees" />
            </div>
            <div className="adb-stat-info">
              <h2 className="adb-stat-number">{totalEmployees}</h2>
              <p className="adb-stat-label">
                Employees
                {newEmployeesThisMonth > 0 && (
                  <span className="adb-stat-delta adb-stat-delta-positive">
                    {" "}+{newEmployeesThisMonth} this month
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* ---------------- Middle Row: Company Overview + Assessments Overview ---------------- */}
        <div className="adb-middle-row">
          {/* Company Overview (Line chart) */}
          <div className="adb-card adb-company-overview-card">
            <div className="adb-card-header-row">
              <h3 className="adb-card-heading">Company Overview</h3>
              {/* Calendar date filter (native date picker with Clear / Today) */}
              <div className="adb-dropdown-wrapper">
                <label className="adb-dropdown-btn" style={{ cursor: "pointer" }}>
                  <img src="/Ass_db_icon_04.png" alt="Calendar" className="adb-dropdown-icon" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                    style={{
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      font: "inherit",
                      color: "inherit",
                      cursor: "pointer",
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="adb-chart-wrapper" style={{ overflowX: "auto" }}>
              {companyOverviewChartData.length === 0 ? (
                <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: "#9CA3AF" }}>
                  No companies added yet.
                </div>
              ) : (
                <div style={{ width: companyOverviewChartWidth, height: 280 }}>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart
                      data={companyOverviewChartData}
                      margin={{ top: 30, right: 10, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid
                        vertical={true}
                        horizontal={true}
                        stroke="#D1D5DB"
                        strokeDasharray="4 4"
                      />
                      <XAxis
                        dataKey="month"
                        tickLine={false}
                        axisLine={{ stroke: "#E5E7EB" }}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                        interval={0}
                      />
                      <YAxis
                        width={24}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 12 }}
                        allowDecimals={false}
                        ticks={companyOverviewYTicks}
                        domain={[0, companyOverviewYTicks[companyOverviewYTicks.length - 1]]}
                      />
                      <Tooltip />
                      {selectedDayLabel && (
                        <ReferenceLine x={selectedDayLabel} stroke="#8B5CF6" strokeDasharray="3 3" />
                      )}
                      <Line
                        type="linear"
                        dataKey="value"
                        name="Companies"
                        stroke="#8B5CF6"
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: "#8B5CF6", strokeWidth: 0 }}
                        activeDot={{ r: 6 }}
                        label={{ position: "top", fill: "#6B7280", fontSize: 11 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Assessments Overview (Donut chart) */}
          <div className="adb-card adb-assessments-overview-card">
            <div className="adb-card-header-row adb-card-header-row-wrap">
              <h3 className="adb-card-heading">Assessments Overview</h3>
              <div className="adb-dropdown-group">
                <AdbSelect
                  icon="/Ass_db_icon_04.png"
                  iconAlt="Calendar"
                  value={assessmentYear}
                  options={yearOptions}
                  onChange={setAssessmentYear}
                />
                <AdbSelect
                  icon="/Ass_db_icon_05.png"
                  iconAlt="List"
                  value={activeCompany}
                  options={companyOptions}
                  onChange={setSelectedCompany}
                />
              </div>
            </div>

            <div className="adb-donut-section">
              <div className="adb-donut-chart-wrapper">
                <ResponsiveContainer width={180} height={180}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={62}
                      outerRadius={88}
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={0}
                      stroke="none"
                    >
                      {donutData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="adb-donut-center-label">
                  <span className="adb-donut-center-number">{totalAttendees}</span>
                  <span className="adb-donut-center-text">Attendees</span>
                </div>
              </div>

              <div className="adb-donut-legend">
                {assessmentOverviewData.map((item) => (
                  <div className="adb-donut-legend-item" key={item.name}>
                    <span
                      className="adb-legend-dot"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="adb-legend-label">{item.name}</span>
                    <span className="adb-legend-value">
                      {item.value}({item.percent}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="adb-card-divider" />

            <div className="adb-view-all-row adb-view-all-row-centered">
              <a href="/admin/assessment/employees" className="adb-view-all-link">
                View All Reports
                <img src="/Ass_db_icon_06.png" alt="Arrow" className="adb-arrow-icon" />
              </a>
            </div>
          </div>
        </div>

        {/* ---------------- Recent Companies Table ---------------- */}
        <div className="adb-card adb-recent-companies-card">
          <div className="adb-recent-companies-header">
            <h3 className="adb-card-heading">Recent Companies</h3>
          </div>

          <div className="adb-table-wrapper">
            <table className="adb-companies-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Company</th>
                  <th>Assessment Year</th>
                  <th>Employees</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: "28px 0", color: "#9CA3AF" }}>
                      No companies added yet.
                    </td>
                  </tr>
                ) : (
                  recentCompanies.map((row) => {
                    const meta = statusMeta[row.status];
                    return (
                      <tr key={row.id}>
                        <td>{row.sNo}</td>
                        <td>{row.company}</td>
                        <td>{row.year}</td>
                        <td>{row.employees}</td>
                        <td>
                          <span
                            className="adb-status-badge"
                            style={{ color: meta.text, backgroundColor: meta.bg }}
                          >
                            <span className="adb-status-dot" style={{ backgroundColor: meta.dot }} />
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <div className="adb-progress-cell">
                            <span className="adb-progress-percent">{row.progress}%</span>
                            <div className="adb-progress-track">
                              <div
                                className="adb-progress-fill"
                                style={{
                                  width: `${row.progress}%`,
                                  backgroundColor: progressColor[row.status],
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="adb-action-buttons">
                            <button
                              className="adb-action-btn adb-action-edit"
                              type="button"
                              onClick={() => openEditModal(row)}
                            >
                              <img
                                src="/db-gl-05.png"
                                alt="Edit"
                                className="adb-edit-icon"
                              />
                              <span>Edit</span>
                            </button>
                            <button
                              className="adb-action-btn adb-action-delete"
                              type="button"
                              onClick={() => openDeleteModal(row)}
                            >
                              <img
                                src="/db-gl-06.png"
                                alt="Delete"
                                className="adb-delete-icon"
                              />
                              <span>Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="adb-view-all-row adb-view-all-row-centered">
            <a href="/admin/assessment/company-report" className="adb-view-all-link">
              View All Companies
              <img src="/Ass_db_icon_06.png" alt="Arrow" className="adb-arrow-icon" />
            </a>
          </div>
        </div>

        {/* ---------------- Edit Company Report popup ---------------- */}
        {isEditModalOpen && (
          <div className="adb-modal-overlay" onClick={closeEditModal}>
            <div
              className="adb-modal-card adb-edit-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="adb-modal-title">Edit Company Report</h3>
              <p className="adb-modal-subtitle">Update Existing Company Details</p>

              <div className="adb-modal-field">
                <label className="adb-modal-label">Company Name</label>
                <input
                  type="text"
                  className="adb-modal-input"
                  placeholder="Enter the Company Name"
                  value={editCompanyName}
                  onChange={(e) => setEditCompanyName(e.target.value)}
                />
              </div>

              <div className="adb-modal-field-row">
                <div className="adb-modal-field">
                  <label className="adb-modal-label">Assessment Year</label>
                  <input
                    type="text"
                    className="adb-modal-input"
                    placeholder="Enter the year"
                    value={editAssessmentYear}
                    onChange={(e) => setEditAssessmentYear(e.target.value)}
                  />
                </div>
                <div className="adb-modal-field">
                  <label className="adb-modal-label">Employees</label>
                  <input
                    type="text"
                    className="adb-modal-input"
                    placeholder="Enter the Employees Count"
                    value={editEmployees}
                    onChange={(e) => setEditEmployees(e.target.value)}
                  />
                </div>
              </div>

              <div className="adb-modal-actions">
                <button
                  type="button"
                  className="adb-modal-btn adb-modal-btn-cancel"
                  onClick={closeEditModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="adb-modal-btn adb-modal-btn-save"
                  onClick={handleSaveEdit}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- Delete confirmation popup ---------------- */}
        {isDeleteModalOpen && (
          <div className="adb-modal-overlay" onClick={closeDeleteModal}>
            <div
              className="adb-modal-card adb-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="adb-delete-warning-icon">
                <AdbIconWarning />
              </div>
              <h3 className="adb-modal-title adb-delete-modal-title">Delete Information</h3>
              <p className="adb-modal-subtitle adb-delete-modal-subtitle">
                This Record will be deleted
              </p>

              <div className="adb-modal-actions adb-modal-actions-centered">
                <button
                  type="button"
                  className="adb-modal-btn adb-modal-btn-keep"
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="adb-modal-btn adb-modal-btn-delete"
                  onClick={handleConfirmDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssessmentDashboardPage;